/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Demand-source test diagnosis (#895 · R3 of the #851 design review).
 *
 * Two things the quick-test panel could not say before:
 *
 *  1. **What was observed**, decomposed. The harness collapsed three separate
 *     facts into one pass/fail, so "✓ Test Passed HTTP 200" covered a 200 with
 *     zero ads, and "✗ Test Failed" covered both an unreachable host and an
 *     exchange that answered with a refusal. {@link deriveTestOutcome} splits
 *     them into Reached · Answered · Filled.
 *
 *     This **decomposes** the existing verdict, it does not reverse #883.
 *     `runDemandSourceTest`'s `transportOk = status >= 200 && status < 400`
 *     survives unchanged as **Answered** — a 2xx no-bid still passes it, because
 *     the exchange behaved correctly and having nothing to sell is not a
 *     configuration fault. What is added is the third observation; what is
 *     removed is the collapse. Do not tighten the backend's `transportOk` to
 *     require parsed ads: that reports a config failure when the exchange was
 *     right, which is the #883 confusion inverted.
 *
 *  2. **What bidBrain thinks is wrong.** {@link diagnoseTest} reads signals the
 *     harness already holds — the payload actually sent, the response actually
 *     received, and the source's own configuration — into a ranked list of
 *     candidate causes. No new intelligence and no inference the operator cannot
 *     check: every cause either points at a fact on the wire or says plainly
 *     that it is a coverage gap worth checking.
 *
 * Two honesty rules are structural here rather than editorial:
 *
 *  - **Rank, and state confidence.** `likely` is reserved for a cause verifiable
 *    in the payload sent. `worth-checking` is for coverage gaps. `exchange-said`
 *    quotes the response and attributes it to them.
 *  - **Never guess silently.** When nothing correlates, {@link diagnoseTest}
 *    returns one `no-signal` cause that says so and points at the evidence,
 *    which is a better claim than an empty panel above a JSON dump.
 */

import type { DemandSource } from '../types';
import type { QuickTestResult } from '../services/demand-source.service';
import type { DriverCapture } from '../components/shared/RequestResponseInspector';
import { getMissingStandardFields, ORTB_STANDARD_SECTIONS } from '@ortb-field-catalog';
import {
  BID_REJECTION_LABELS,
  describeNoBidReason,
  REQUESTED_BID_CURRENCY,
  type BidRejectionCount,
} from '@bid-rejection';
import { extractProtocolVersion, paramsDtoToRows } from '../components/shared/editors/parameter-editor.adapters';

// ---------------------------------------------------------------------------
// The three-part outcome
// ---------------------------------------------------------------------------

/**
 * State of one observation.
 *
 * `none` is deliberately distinct from `fail`: an exchange that answers and
 * declines to bid produced a real negative observation, not an error. Rendering
 * the two the same is what let a no-bid read as a broken source.
 *
 * `incomplete` is Filled's alone: ads came back, but not with what the optimizer
 * needs to use them. R2's state table had no name for this — it defines
 * "Answering, not filling" as *zero* ads parsed and "Proven" as ads parsed
 * *with optimizer-critical fields populated*, so a source returning three ads
 * with no bid satisfied neither. Counting it as Filled would let increment 3
 * stamp **Proven** on a source the optimizer cannot rank, which is the exact
 * dishonesty R2 exists to remove, one level deeper and more convincing because
 * an observation really was made. Ratified as the badge state
 * **`Filling, values missing`** (#897).
 *
 * Named `incomplete`, deliberately **not** `partial`: `BADGE_STATES.partial` is
 * already `"Answering, not filling"` — the state this one is distinguished from.
 * One word meaning two opposite things across adjacent modules is a trap.
 */
export type ObservationState = 'ok' | 'fail' | 'none' | 'incomplete' | 'unobserved';

export interface TestObservation {
  label: 'Reached' | 'Answered' | 'Filled';
  state: ObservationState;
  /** One clause, lower case, completing the label. */
  detail: string;
}

export type OutcomeTone = 'success' | 'warning' | 'error';

export interface TestOutcome {
  /** bidBrain's claim about itself: did the request leave and come back. */
  reached: TestObservation;
  /** The exchange's verdict, never the harness's. */
  answered: TestObservation;
  /** The only observation that licenses the word Proven. */
  filled: TestObservation;
  /** Summary claim for the panel header. */
  summary: { label: string; tone: OutcomeTone };
}

// ---------------------------------------------------------------------------
// The diagnosis
// ---------------------------------------------------------------------------

/**
 * How far the evidence goes. Rendered verbatim as the lead-in of each cause, so
 * the operator can weigh a claim without reading the code that made it.
 */
export type DiagnosisConfidence = 'likely' | 'worth-checking' | 'exchange-said' | 'no-signal';

/** Where a fix is made. The panel stages an edit there — never a nav hop. */
export type DiagnosisFixTarget = 'connection' | 'params' | 'headers' | 'ortbBody' | 'responseMapping';

export interface DiagnosisFix {
  target: DiagnosisFixTarget;
  /** Path/name within the target surface, when the cause names one. */
  path?: string;
}

export interface DiagnosisCause {
  confidence: DiagnosisConfidence;
  /** The claim, one sentence, in the operator's terms. */
  message: string;
  /** The field/path/param the claim points at. Rendered mono. */
  ref?: string;
  fix?: DiagnosisFix;
}

export interface TestDiagnosisInput {
  result: QuickTestResult | null;
  /** Transport error from the admin API call itself — the harness never ran. */
  requestError?: Error | null;
  /** The configuration under test: the wizard's live form, or the saved row. */
  source?: Partial<DemandSource> | null;
}

/** The lead-in rendered before each cause. */
export const CONFIDENCE_LABEL: Record<DiagnosisConfidence, string> = {
  likely: 'Most likely',
  'worth-checking': 'Worth checking',
  'exchange-said': 'The exchange said',
  'no-signal': 'No signal',
};

// ---------------------------------------------------------------------------
// Optimizer-critical fields on a parsed ad
// ---------------------------------------------------------------------------

/** The subset of a parsed ad this module reads. Mirrors `VastAdDto`. */
export interface ParsedAd {
  adId?: string;
  bid?: number;
  duration?: string;
  durationSeconds?: number;
  mediaUrl?: string;
  adDomain?: string;
  iabCategory?: string;
  /**
   * The ad is a VAST `<Wrapper>` — a redirect, not a creative. It carries no
   * `<MediaFiles>` and no `<Duration>` by design; both resolve when the player follows
   * `VASTAdTagURI` (#958).
   */
  isWrapper?: boolean;
}

/**
 * "Optimizer-critical" is defined against **what the optimizer can actually
 * consume**, not against what the mapping editor can express.
 *
 * `BidRefDto` (`libs/dto/src/vast.dto.ts`) is the projection of a parsed ad that
 * reaches pod-level optimization, and within it `libs/optimizers` reads
 * `durationSeconds` and `bid` overwhelmingly (99 and 87 call sites), with
 * `iabCategory` a distant third and `adDomain` used by `libs/rules` for
 * competitive separation. So `bid` and duration are `required`; the rest matter
 * only when a rule references them and are reported as `advisory`.
 *
 * Gating Proven on anything richer would be dishonest in the other direction:
 * #871 records that response-mapped *custom* fields never reach `BidRefDto` at
 * all, so a source could map them perfectly and the optimizer would still never
 * see them. Widen this list when #871 widens what the optimizer consumes.
 */
type AdFieldSeverity = 'required' | 'advisory';

export interface AdFieldGap {
  field: string;
  severity: AdFieldSeverity;
  /** How many of the parsed ads are missing it. */
  adsAffected: number;
  /** Operator-facing consequence — why this matters, not just that it is absent. */
  consequence: string;
  /** True when the value is definitely absent; false when it is inferred. */
  certain: boolean;
}

/** The driver default both protocols fall back to when duration doesn't extract. */
const DEFAULT_DURATION_SECONDS = 30;

/** One optimizer-consumed field, and what its absence costs. */
export interface OptimizerFieldSpec {
  field: keyof ParsedAd;
  /** Column header / cause subject. */
  label: string;
  severity: AdFieldSeverity;
  /** Operator-facing consequence — why this matters, not just that it is absent. */
  consequence: string;
  /** True when this ad carries a usable value. */
  isPopulated: (ad: ParsedAd) => boolean;
  /** How the value reads in the produced-ads table. */
  format: (ad: ParsedAd) => string;
}

/**
 * The optimizer-consumed fields, declared once.
 *
 * Both the gap diagnosis and the produced-ads table read this list, so they
 * cannot disagree about what counts as required — a table showing a green
 * `duration` beside a diagnosis calling it missing would destroy trust in both.
 *
 * Order is the order they appear as columns, most consequential first.
 */
export const OPTIMIZER_FIELDS: OptimizerFieldSpec[] = [
  {
    field: 'bid',
    label: 'bid',
    severity: 'required',
    consequence: 'the optimizer cannot rank these ads by value, so yield decisions fall back to ordering',
    // A zero bid is populated, not missing — #870 makes no-bid a real scenario.
    isPopulated: (ad) => typeof ad.bid === 'number' && Number.isFinite(ad.bid),
    format: (ad) => (typeof ad.bid === 'number' ? String(ad.bid) : ''),
  },
  {
    field: 'duration',
    label: 'duration',
    severity: 'required',
    consequence: `the pod builder is using the driver's ${DEFAULT_DURATION_SECONDS}-second default, which decides how many ads fit the break`,
    // Set only when the response actually carried one, on BOTH protocols — the
    // ORTB driver no longer forges '00:00:30' when extraction fails, which is
    // what makes this conclusive rather than a guess. `bid.dur` counts as carried:
    // it is the bidder telling us, and for a wrapper it is the only duration
    // there is (#958).
    isPopulated: (ad) => Boolean(ad.duration),
    format: (ad) => ad.duration ?? `${ad.durationSeconds ?? DEFAULT_DURATION_SECONDS}s (default)`,
  },
  {
    field: 'mediaUrl',
    label: 'media',
    severity: 'required',
    consequence: 'there is no creative to serve, so these ads cannot be delivered even if selected',
    /**
     * **A wrapper has no media URL, and that is not a gap (#958).** A `<Wrapper>` is a
     * redirect: the creative lives behind `VASTAdTagURI` and the player resolves it at
     * play time. Reporting it as missing described a perfectly good FreeWheel response
     * as a broken integration, which is the failure this whole diagnosis exists to
     * prevent — it must separate absence that is expected from absence that is a defect.
     */
    isPopulated: (ad) => Boolean(ad.mediaUrl) || ad.isWrapper === true,
    format: (ad) => ad.mediaUrl ?? (ad.isWrapper ? 'wrapper — resolves at play time' : ''),
  },
  {
    field: 'adId',
    label: 'adId',
    severity: 'advisory',
    consequence: 'the ad cannot be identified in reporting or de-duplicated across sources',
    isPopulated: (ad) => Boolean(ad.adId),
    format: (ad) => ad.adId ?? '',
  },
  {
    field: 'adDomain',
    label: 'adDomain',
    severity: 'advisory',
    consequence: 'competitive-separation rules cannot evaluate these ads',
    isPopulated: (ad) => Boolean(ad.adDomain),
    format: (ad) => ad.adDomain ?? '',
  },
  {
    field: 'iabCategory',
    label: 'iabCategory',
    severity: 'advisory',
    consequence: 'category exclusion rules cannot evaluate these ads',
    isPopulated: (ad) => Boolean(ad.iabCategory),
    format: (ad) => ad.iabCategory ?? '',
  },
];

/** Which optimizer-critical fields the parsed ads are missing. */
export function findOptimizerFieldGaps(ads: readonly ParsedAd[]): AdFieldGap[] {
  if (ads.length === 0) return [];
  const gaps: AdFieldGap[] = [];

  for (const spec of OPTIMIZER_FIELDS) {
    const missing = ads.filter((ad) => !spec.isPopulated(ad));
    if (missing.length === 0) continue;
    gaps.push({
      field: spec.label,
      severity: spec.severity,
      adsAffected: missing.length,
      consequence: spec.consequence,
      certain: true,
    });
  }

  return gaps;
}

// ---------------------------------------------------------------------------
// Reading the result body
// ---------------------------------------------------------------------------

interface TestBodyFacts {
  capture?: DriverCapture;
  /** Ads the driver parsed out of the response. `undefined` = not reported. */
  ads?: ParsedAd[];
  /** Ads parsed out of the response by the driver. `undefined` = not reported. */
  adsCount?: number;
  /** Harness-level failure detail (driver threw before/around the request). */
  detail?: string;
  endpoint?: string;
  hint?: string;
  /**
   * What the test asked for (`testContext.pod`). Mirrors the server's
   * `demand-source-test-outcome.ts` — the two derive the same sentence, and the
   * server's is the one that gets persisted (#897 slice B).
   */
  requested?: { maxAds?: number; maxDuration?: number };
  /** OpenRTB `nbr`, as the driver read it off the response (`#1318` item 6). */
  noBidReason?: number;
  /** Ads the source returned and bidBrain discarded at ingestion, by reason. */
  rejectedAds?: BidRejectionCount[];
}

/** Pull the facts the outcome and the diagnosis both read off the result body. */
export function readTestBody(result: QuickTestResult | null): TestBodyFacts {
  const body =
    result?.body != null && typeof result.body === 'object' ? (result.body as Record<string, unknown>) : null;
  if (!body) return {};

  const ads = (body.ads as { ads?: unknown[] } | undefined)?.ads;
  return {
    capture: body.capture as DriverCapture | undefined,
    ads: Array.isArray(ads) ? (ads as ParsedAd[]) : undefined,
    adsCount: Array.isArray(ads) ? ads.length : undefined,
    detail: typeof body.detail === 'string' ? body.detail : undefined,
    endpoint: typeof body.endpoint === 'string' ? body.endpoint : undefined,
    hint: typeof body.hint === 'string' ? body.hint : undefined,
    requested: (body.testContext as { pod?: { maxAds?: number; maxDuration?: number } } | undefined)?.pod,
    noBidReason: typeof body.noBidReason === 'number' ? body.noBidReason : undefined,
    rejectedAds: Array.isArray(body.rejectedAds) ? (body.rejectedAds as BidRejectionCount[]) : undefined,
  };
}

/** Host of a URL, for the Reached claim. Falls back to the raw string. */
function hostOf(url?: string): string {
  if (!url) return 'the endpoint';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

// ---------------------------------------------------------------------------
// deriveTestOutcome
// ---------------------------------------------------------------------------

/**
 * Decompose a quick-test result into the three observations.
 *
 * Reached is true when bytes came back at all — that is bidBrain's own claim,
 * and it is the only one that is false when DNS fails or the connection is
 * refused. Answered reports the exchange's HTTP verdict. Filled reports parsed
 * ads, and an HTTP 200 with zero ads is never summarised as a success: it
 * summarises as **Answering, not filling**, which is the vocabulary the
 * readiness state (R2, increment 3) will persist.
 */
export function deriveTestOutcome(input: TestDiagnosisInput): TestOutcome {
  const { result, requestError } = input;
  const facts = readTestBody(result);
  const capture = facts.capture;
  const host = hostOf(capture?.request?.url ?? facts.endpoint);

  // The admin API itself never answered — nothing at all was observed.
  if (!result) {
    return {
      reached: {
        label: 'Reached',
        state: 'fail',
        detail: requestError ? `the test could not be run — ${requestError.message}` : 'not yet run',
      },
      answered: { label: 'Answered', state: 'unobserved', detail: 'not yet observed' },
      filled: { label: 'Filled', state: 'unobserved', detail: 'not yet observed' },
      summary: {
        label: requestError ? 'Test could not run' : 'Not yet tested',
        tone: requestError ? 'error' : 'warning',
      },
    };
  }

  const response = capture?.response;
  const reachedOk = response != null;

  if (!reachedOk) {
    // The driver threw — no response was received. `detail` carries the cause
    // (DNS, connection refused, TLS, timeout); it is the whole story here.
    return {
      reached: {
        label: 'Reached',
        state: 'fail',
        detail: facts.detail ? `${host} did not answer — ${facts.detail}` : `${host} did not answer`,
      },
      answered: { label: 'Answered', state: 'unobserved', detail: 'not yet observed' },
      filled: { label: 'Filled', state: 'unobserved', detail: 'not yet observed' },
      summary: { label: 'Not reached', tone: 'error' },
    };
  }

  const status = response.status ?? result.status;
  const timing = Number.isFinite(response.timingMs) ? `${Math.round(response.timingMs)} ms` : null;
  const reached: TestObservation = {
    label: 'Reached',
    state: 'ok',
    detail: timing ? `${host} answered in ${timing}` : `${host} answered`,
  };

  // Answered mirrors the backend's `transportOk` exactly (#883): 2xx/3xx is the
  // exchange behaving correctly, whether or not it had anything to sell.
  const answeredOk = status >= 200 && status < 400;
  const answered: TestObservation = {
    label: 'Answered',
    state: answeredOk ? 'ok' : 'fail',
    detail: answeredOk
      ? `the exchange accepted the request. HTTP ${status}.`
      : `the exchange refused the request. HTTP ${status}.`,
  };

  if (!answeredOk) {
    return {
      reached,
      answered,
      filled: { label: 'Filled', state: 'unobserved', detail: 'not yet observed' },
      summary: { label: 'Refused', tone: 'error' },
    };
  }

  const adsCount = facts.adsCount;
  if (adsCount == null) {
    return {
      reached,
      answered,
      filled: { label: 'Filled', state: 'unobserved', detail: 'no ad count reported' },
      summary: { label: 'Answering, not filling', tone: 'warning' },
    };
  }

  if (adsCount === 0) {
    return {
      reached,
      answered,
      filled: {
        label: 'Filled',
        state: 'none',
        detail: facts.requested?.maxAds
          ? `the exchange returned no ads (${facts.requested.maxAds} requested)`
          : 'the exchange returned no ads',
      },
      summary: { label: 'Answering, not filling', tone: 'warning' },
    };
  }

  // Ads came back — but Filled is only met when they are *usable*. R2 defines
  // Proven as "at least one ad parsed with optimizer-critical fields populated",
  // and this observation is what increment 3 will map onto that badge.
  const gaps = findOptimizerFieldGaps(facts.ads ?? []);
  const blocking = gaps.filter((g) => g.severity === 'required');
  // "3 of 7 ads" — see the note in `demand-source-test-outcome.ts` (#1011).
  const requestedAds = facts.requested?.maxAds;
  const count =
    typeof requestedAds === 'number' && requestedAds > 0
      ? `${adsCount} of ${requestedAds} ad${requestedAds === 1 ? '' : 's'} parsed`
      : `${adsCount} ad${adsCount === 1 ? '' : 's'} parsed`;

  if (blocking.length > 0) {
    return {
      reached,
      answered,
      filled: {
        label: 'Filled',
        state: 'incomplete',
        detail: `${count}, but ${blocking.map((g) => g.field).join(', ')} ${
          blocking.length === 1 ? 'is' : 'are'
        } not populated`,
      },
      summary: { label: 'Filling, values missing', tone: 'warning' },
    };
  }

  return {
    reached,
    answered,
    filled: {
      label: 'Filled',
      state: 'ok',
      detail: `${count} from the response`,
    },
    summary: { label: 'Filled', tone: 'success' },
  };
}

// ---------------------------------------------------------------------------
// Payload walking
// ---------------------------------------------------------------------------

/** A value that is *entirely* one macro token — the case the runtime drops. */
const WHOLLY_MACRO = /^\{([a-zA-Z][\w.-]*)\}$/;
/** Any macro token embedded in a string — the case the runtime sends verbatim. */
const EMBEDDED_MACRO = /\{([a-zA-Z][\w.-]*)\}/g;

/** How many individual macro causes to list before summarising the remainder. */
const MAX_MACRO_CAUSES = 3;

function parseJson(text?: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Every scalar leaf of a JSON value, keyed by dotted path (`imp.0.tagid`). */
export function collectLeaves(node: unknown, base = '', out = new Map<string, unknown>()): Map<string, unknown> {
  if (node == null || typeof node !== 'object') {
    if (base) out.set(base, node);
    return out;
  }
  if (Array.isArray(node)) {
    node.forEach((item, i) => collectLeaves(item, base ? `${base}.${i}` : String(i), out));
    return out;
  }
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    collectLeaves(value, base ? `${base}.${key}` : key, out);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Cause 0 — the ads came back, but not usable
// ---------------------------------------------------------------------------

/**
 * Leads the list whenever it applies, ahead of the review's five failure causes,
 * because it is the whole answer for a result that filled without being usable:
 * the response arrived, the mapping read it, and what it produced is missing
 * something the optimizer needs. Every other cause is about a request that
 * failed, which this one did not.
 *
 * The fix is always the response mapping — this is the one class of failure that
 * is definitionally about how the response is *read* rather than what was sent.
 */
function diagnoseAdFieldGaps(ads: readonly ParsedAd[] | undefined): DiagnosisCause[] {
  if (!ads || ads.length === 0) return [];

  return findOptimizerFieldGaps(ads).map((gap) => ({
    // A required field absent from the parsed ads is a fact, so `likely`. An
    // ADVISORY gap never is: it did not stop anything, and it only matters if a
    // rule references it. Ranking five gaps "Most likely" at once — which is
    // what an all-fields-missing response produced — makes the ranking say
    // nothing, and the whole point of stating confidence is that the operator
    // can tell the claim they should act on from the one they can note.
    // A duration that merely *reads* as the driver default is also not a fact:
    // it cannot be told from a genuine 30-second ad.
    confidence: gap.severity === 'advisory' || !gap.certain ? ('worth-checking' as const) : ('likely' as const),
    message:
      `${gap.field} is not populated on ${
        gap.adsAffected === ads.length ? 'any' : `${gap.adsAffected} of the ${ads.length}`
      } ad${ads.length === 1 ? '' : 's'} that came back — ${gap.consequence}.` +
      (gap.severity === 'advisory' ? ' Only matters if a rule references it.' : ''),
    ref: gap.field,
    fix: { target: 'responseMapping' as const, path: gap.field },
  }));
}

// ---------------------------------------------------------------------------
// Cause 1 — protocol mismatch (guidance §14)
// ---------------------------------------------------------------------------

/**
 * URL parameters on an OpenRTB source. The ORTB driver POSTs its endpoint
 * verbatim and carries device/user/consent in the body, so a configured
 * parameter does not become the field it names — `applyRequestFieldDecorations`
 * lands it in `ext.<name>`. When that is visible in the body actually sent it is
 * a fact rather than a suspicion, so the claim is ranked accordingly.
 */
function diagnoseProtocolMismatch(
  source: Partial<DemandSource> | null | undefined,
  sentBody: Map<string, unknown> | null,
): DiagnosisCause[] {
  if (!source || source.method !== 'ORTB') return [];

  const rows = paramsDtoToRows(source.params);
  const names = rows.map((r) => r.name.trim()).filter(Boolean);
  if (names.length === 0) return [];

  const onTheWire = sentBody
    ? names.filter((name) => {
        for (const path of sentBody.keys()) {
          if (path === `ext.${name}` || path.startsWith(`ext.${name}.`)) return true;
        }
        return false;
      })
    : [];

  const list = names.slice(0, 4).join(', ') + (names.length > 4 ? `, +${names.length - 4} more` : '');

  if (onTheWire.length > 0) {
    return [
      {
        confidence: 'likely',
        message:
          `${onTheWire.length} URL parameter${onTheWire.length === 1 ? '' : 's'} on an OpenRTB source ` +
          `${onTheWire.length === 1 ? 'was' : 'were'} sent as ext.${onTheWire[0]}` +
          `${onTheWire.length > 1 ? ' and siblings' : ''}, not as the OpenRTB field ` +
          `${onTheWire.length === 1 ? 'it names' : 'they name'}. OpenRTB carries these in the body.`,
        ref: onTheWire.join(', '),
        fix: { target: 'ortbBody' },
      },
    ];
  }

  return [
    {
      confidence: 'worth-checking',
      message:
        `This OpenRTB source carries ${names.length} URL parameter${names.length === 1 ? '' : 's'}. ` +
        'OpenRTB POSTs its endpoint verbatim, so these land in ext rather than the fields they name.',
      ref: list,
      fix: { target: 'params' },
    },
  ];
}

// ---------------------------------------------------------------------------
// Cause 2 — unresolved macros in what was actually sent
// ---------------------------------------------------------------------------

/**
 * What an unresolved macro does is surface-specific and deliberate, so the
 * evidence differs per surface (see CLAUDE.md, "Macro resolution semantics"):
 *
 *  - **ORTB body leaf** — the key is *omitted* (#880), because a literal
 *    `{token}` is a type/enum violation an exchange may reject outright. So the
 *    detectable fact is an authored path that is missing from the body sent.
 *  - **Header** — left literal, inert to the receiver and visible here.
 *  - **VAST URL param** — omitted when optional, empty when required.
 *
 * A composed string (`rating-{contentRating}`) is the operator's own text and is
 * sent as written on every surface, so it shows up literally wherever it appears.
 */
function diagnoseUnresolvedMacros(
  source: Partial<DemandSource> | null | undefined,
  capture: DriverCapture | undefined,
  sentBody: Map<string, unknown> | null,
): DiagnosisCause[] {
  if (!capture) return [];
  const causes: DiagnosisCause[] = [];

  // (a) Literal tokens on the wire — URL, headers, and composed body leaves.
  const literalInUrl = new Set<string>();
  for (const [, token] of (capture.request?.url ?? '').matchAll(EMBEDDED_MACRO)) {
    literalInUrl.add(token);
  }
  if (literalInUrl.size > 0) {
    const tokens = [...literalInUrl];
    causes.push({
      confidence: 'likely',
      message:
        `The request URL was sent with ${tokens.length === 1 ? 'the literal macro' : 'literal macros'} ` +
        `${tokens.map((t) => `{${t}}`).join(', ')} — ${tokens.length === 1 ? 'it' : 'they'} did not resolve.`,
      ref: tokens.map((t) => `{${t}}`).join(', '),
      fix: { target: 'params' },
    });
  }

  for (const [name, value] of Object.entries(capture.request?.headers ?? {})) {
    const tokens = [...String(value).matchAll(EMBEDDED_MACRO)].map((m) => m[1]);
    if (tokens.length === 0) continue;
    causes.push({
      confidence: 'likely',
      message:
        `Header ${name} was sent with the literal macro ${tokens.map((t) => `{${t}}`).join(', ')}. ` +
        'Unresolved header macros are left as written, so the exchange received the token.',
      ref: name,
      fix: { target: 'headers', path: name },
    });
  }

  if (sentBody) {
    for (const [path, value] of sentBody) {
      if (typeof value !== 'string') continue;
      const tokens = [...value.matchAll(EMBEDDED_MACRO)].map((m) => m[1]);
      if (tokens.length === 0) continue;
      causes.push({
        confidence: 'likely',
        message:
          `${path} was sent as "${value}" — ${tokens.map((t) => `{${t}}`).join(', ')} did not resolve ` +
          'and the composed value went out as written.',
        ref: path,
        fix: { target: 'ortbBody', path },
      });
    }
  }

  // (b) Authored ORTB body leaves that are wholly a macro and are absent from
  //     what was sent. The runtime drops the key rather than shipping a literal,
  //     so the only evidence is the hole it left.
  const template = source?.ortbBodyTemplate?.body;
  if (template && sentBody) {
    for (const [path, value] of collectLeaves(template)) {
      if (typeof value !== 'string') continue;
      const match = WHOLLY_MACRO.exec(value);
      if (!match) continue;
      if (sentBody.has(path)) continue;
      causes.push({
        confidence: 'likely',
        message:
          `${path} is mapped to {${match[1]}}, which did not resolve, so the field was omitted from ` +
          'the request. An unresolved macro drops the key rather than sending the token.',
        ref: path,
        fix: { target: 'ortbBody', path },
      });
    }
  }

  // A source with a broken macro vocabulary can produce dozens of these, and a
  // wall of causes is as unreadable as none. Cap the list — and say what was cut
  // rather than truncating silently, which would read as "that was all of it".
  if (causes.length > MAX_MACRO_CAUSES) {
    const dropped = causes.length - MAX_MACRO_CAUSES;
    return [
      ...causes.slice(0, MAX_MACRO_CAUSES),
      {
        confidence: 'likely',
        message:
          `${dropped} further unresolved macro${dropped === 1 ? '' : 's'} ${dropped === 1 ? 'was' : 'were'} ` +
          'found in this request. The request sent below is the full list.',
      },
    ];
  }

  return causes;
}

// ---------------------------------------------------------------------------
// Cause 3 — required or recommended fields with no mapping
// ---------------------------------------------------------------------------

/**
 * Read against the body **actually sent**, not the authored template. The driver
 * builds a procedural merge base underneath the operator's document, so a field
 * missing from the template is usually present on the wire — and a field missing
 * from the wire is genuinely missing, whoever was supposed to supply it.
 */
function diagnoseMissingFields(
  source: Partial<DemandSource> | null | undefined,
  sentBody: Map<string, unknown> | null,
): DiagnosisCause[] {
  if (!source || source.method !== 'ORTB' || !sentBody) return [];

  const version = extractProtocolVersion(source, '2.6');
  const missing: Array<{ section: string; path: string; description: string }> = [];

  for (const section of ORTB_STANDARD_SECTIONS) {
    // `imp` is an array on the wire; the catalog describes one impression.
    const prefix = section === 'imp' ? 'imp.0.' : `${section}.`;
    const present = new Set<string>();
    let sectionSeen = false;
    for (const path of sentBody.keys()) {
      if (!path.startsWith(prefix)) continue;
      sectionSeen = true;
      present.add(path.slice(prefix.length));
    }
    // A section the source doesn't use at all (site on an app source) is not a gap.
    if (!sectionSeen) continue;

    // `replace`: this reads the body that was **sent**, which already carries everything the driver
    // built beneath the template, so nothing further is supplied.
    for (const field of getMissingStandardFields(section, present, version, 'replace')) {
      if (field.importance !== 'required') continue;
      // `?? ''` rather than a written-in phrase: a declaration that describes nothing should read
      // as nothing here, not as invented copy about a field we cannot describe.
      missing.push({ section, path: `${section}.${field.name}`, description: field.description ?? '' });
    }
  }

  if (missing.length === 0) return [];

  const head = missing[0];
  const rest = missing.length - 1;
  return [
    {
      confidence: 'worth-checking',
      message:
        `${head.path} (${head.description}) is required by OpenRTB ${version} and was not in the ` +
        `request${rest > 0 ? `, along with ${rest} other required field${rest === 1 ? '' : 's'}` : ''}.`,
      ref: missing.map((m) => m.path).join(', '),
      fix: { target: 'ortbBody', path: head.path },
    },
  ];
}

/** The exchange's no-bid reason, **named** — a bare `7` told an operator nothing (`#1318` item 6). */
function noBidCause(code: number): DiagnosisCause {
  return {
    confidence: 'exchange-said',
    message: `no bid — ${describeNoBidReason(code)} (OpenRTB \`nbr\`).`,
    ref: 'nbr',
  };
}

/**
 * **Ads that came back and were discarded** (`#1318` item 6). Without this a test whose every bid was
 * discarded — priced in another currency, or below the source's own floor — read *Answering, not
 * filling* with nothing to say why, which is the exchange's fault by implication and ours in fact.
 * Keyed on the shared reason labels, so a reason added to the vocabulary is explained here with no
 * edit.
 */
function diagnoseRejectedAds(
  rejected: readonly BidRejectionCount[] | undefined,
  capture: DriverCapture | undefined,
): DiagnosisCause[] {
  return (rejected ?? [])
    .filter((entry) => entry.count > 0)
    .map((entry) => {
      const ads = `${entry.count} ad${entry.count === 1 ? '' : 's'}`;
      let message = `${ads} came back and were discarded: ${BID_REJECTION_LABELS[entry.reason] ?? entry.reason}.`;
      if (entry.reason === 'unexpected-currency') {
        const parsed = capture?.response?.body ? parseJson(capture.response.body) : undefined;
        const cur = parsed && typeof parsed === 'object' ? (parsed as { cur?: unknown }).cur : undefined;
        message =
          `${ads} came back priced in ${typeof cur === 'string' ? cur : 'another currency'}; requests ask for ${REQUESTED_BID_CURRENCY}, ` +
          'and bidBrain holds no exchange rates, so they were discarded.';
      }
      return {
        confidence: 'likely' as const,
        message,
        ...(entry.reason === 'unexpected-currency' ? { ref: 'cur' } : {}),
      };
    });
}

// ---------------------------------------------------------------------------
// Cause 4 — error text lifted verbatim from the response
// ---------------------------------------------------------------------------

/** Keys exchanges commonly put a human-readable refusal under. */
const ERROR_KEYS = ['error', 'message', 'reason', 'detail', 'description', 'errorMessage'];

/**
 * The exchange's own words, quoted and attributed. Never carries a Fix — acting
 * on someone else's error text is the operator's call, not ours.
 */
function diagnoseExchangeText(
  capture: DriverCapture | undefined,
  refused: boolean,
  noBidReason: number | undefined,
): DiagnosisCause[] {
  const raw = capture?.response?.body;
  if (!raw || !raw.trim()) {
    return noBidReason !== undefined ? [noBidCause(noBidReason)] : [];
  }

  // The gate is on *how* the exchange said it, not on the status code.
  //
  // A **structured** field — a JSON `error`/`message`, or OpenRTB's `nbr` — is
  // something the exchange deliberately put there, and it is worth reading on a
  // 200 as much as on a 400: `nbr` is precisely a no-bid explanation delivered
  // with HTTP 200.
  //
  // **Unstructured prose**, mined by stripping tags off a non-JSON body, is a
  // guess about what a document means. Doing that to a successful response
  // produced a VAST document flattened into "bidBrain pool-ad-233
  // advertiser13.example.com 11.41 00:00:30", attributed to the exchange.
  // Attributing our own creative markup to them as a sentence is worse than
  // saying nothing, so that path now requires an actual refusal.

  const parsed = parseJson(raw);
  if (parsed && typeof parsed === 'object') {
    const leaves = collectLeaves(parsed);
    for (const [path, value] of leaves) {
      const key = path.split('.').pop() ?? '';
      if (!ERROR_KEYS.includes(key)) continue;
      if (typeof value !== 'string' || !value.trim()) continue;
      return [{ confidence: 'exchange-said', message: `"${value.trim()}"`, ref: path }];
    }
    // OpenRTB's machine-readable no-bid reason, when there is no prose — the driver's reading first,
    // the body as a fallback for a result from before the driver declared it.
    const nbr = noBidReason ?? leaves.get('nbr');
    if (typeof nbr === 'number') return [noBidCause(nbr)];
    return [];
  }

  // Non-JSON prose. Only on an actual refusal (see above), and never mined out
  // of an XML document — a VAST response stripped of its tags reads as a
  // sentence but is not one. Checked against the RAW body, because the stripping
  // below removes the leading `<?xml` that would have given it away.
  if (!refused) return [];
  if (/^\s*<\?xml/i.test(raw) || /<VAST[\s>]/i.test(raw)) return [];

  // A short plain-text or HTML-stripped refusal is usable ("502 Bad Gateway"); a
  // payload is not — quoting 40 KB of markup is worse than saying nothing.
  const text = raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text || text.length > 300) return [];
  if (text.startsWith('<')) return [];
  return [{ confidence: 'exchange-said', message: `"${text}"` }];
}

// ---------------------------------------------------------------------------
// Cause 5 — the response is not the protocol this source expects
// ---------------------------------------------------------------------------

/**
 * A VAST source answered with JSON, or an OpenRTB source answered with XML. The
 * usual cause is an endpoint pasted from the wrong integration, and it is
 * verifiable from the response bytes.
 *
 * The review's fuller version of this cause compares against the detected
 * preset's body template — presets do not carry one yet, so that half arrives
 * with template coverage (R6, increment 7).
 */
function diagnoseShapeMismatch(
  source: Partial<DemandSource> | null | undefined,
  capture: DriverCapture | undefined,
): DiagnosisCause[] {
  const raw = capture?.response?.body?.trim();
  if (!raw || !source?.method) return [];

  const looksXml = raw.startsWith('<');
  const looksJson = raw.startsWith('{') || raw.startsWith('[');

  if (source.method === 'ORTB' && looksXml) {
    return [
      {
        confidence: 'likely',
        message:
          'This source is configured as OpenRTB but the endpoint answered with XML. ' +
          'That is a VAST endpoint — the protocol or the URL is wrong.',
        fix: { target: 'connection' },
      },
    ];
  }
  if (source.method === 'VAST' && looksJson) {
    return [
      {
        confidence: 'likely',
        message:
          'This source is configured as VAST but the endpoint answered with JSON. ' +
          'That is an OpenRTB endpoint — the protocol or the URL is wrong.',
        fix: { target: 'connection' },
      },
    ];
  }
  return [];
}

// ---------------------------------------------------------------------------
// diagnoseTest
// ---------------------------------------------------------------------------

/** The claim made when nothing correlates — stated, never left blank. */
function noSignalCause(outcome: TestOutcome, facts: TestBodyFacts, ranAtAll: boolean): DiagnosisCause {
  if (!ranAtAll) {
    return {
      confidence: 'no-signal',
      message:
        'The admin API did not answer, so the test never ran and there is nothing to read. ' +
        'Check that the admin service is running and that the demand source is reachable from it.',
    };
  }
  if (outcome.reached.state !== 'ok') {
    return {
      confidence: 'no-signal',
      message:
        facts.hint ??
        'The request never reached the endpoint, so there is nothing from the exchange to read. ' +
          'Check the URL and that the host is reachable from the bidBrain server.',
    };
  }
  if (outcome.answered.state === 'fail') {
    return {
      confidence: 'no-signal',
      message: 'The exchange refused without telling us why. Here is exactly what we sent.',
    };
  }
  return {
    confidence: 'no-signal',
    message:
      'The exchange answered and chose not to bid. Nothing in the request looks wrong from here — ' +
      'a no-bid is a normal answer, not a configuration fault.',
  };
}

/**
 * Does the failure implicate the **endpoint** itself? (#1008)
 *
 * The gap this closes: `connection` was a first-class cause when the request never
 * arrived ("Check the URL and that the host is reachable"), and dropped out entirely
 * on a 4xx — even though **a correct host with a wrong path answers with a 4xx**,
 * which is the most common way an endpoint typo presents. Reported from user testing
 * twice in one session: a 404 and a 400, neither of which said "check the endpoint",
 * while the panel's other text pointed at the payload.
 *
 * Three signals, strongest first:
 *
 * 1. **The exchange echoed the path.** A 404 body like `Cannot POST /ortb/tes` names
 *    the thing that is wrong, and nothing was reading it. That is `likely` by R3's
 *    standard — verifiable in what was actually sent and received.
 * 2. **We recorded that the endpoint changed.** `readiness.demotedBy` includes
 *    `endpoint`, fingerprinted by R2a for exactly this purpose, and the source had
 *    filled before. This is the first diagnosis input that is bidBrain's own record
 *    rather than a reading of the wire, and the only one that can say "this used to
 *    work".
 * 3. **Any 4xx at all**, as a coverage cause — the host answered and refused, so the
 *    path and any routing or auth parameters are the first thing to check.
 */
function diagnoseEndpoint(
  source: Partial<DemandSource> | null | undefined,
  capture: DriverCapture | undefined,
  refused: boolean,
): DiagnosisCause[] {
  if (!refused) return [];

  const sentUrl = capture?.request?.url ?? source?.endpoint ?? '';
  const sentPath = (() => {
    try {
      return new URL(sentUrl).pathname;
    } catch {
      return '';
    }
  })();

  // 1 — the refusal names a path, and it is the one we sent.
  const body = typeof capture?.response?.body === 'string' ? capture.response.body : '';
  const echoed = body.match(/(?:cannot (?:get|post|put|patch|delete)|no route (?:for|matched))\s+(\/[^\s"'<]*)/i);
  if (echoed && sentPath && echoed[1] === sentPath) {
    return [
      {
        confidence: 'likely',
        message:
          `The exchange refused and named the path it could not route: "${echoed[1]}". ` +
          'That is the path this source is configured to call, so the endpoint is wrong — ' +
          'check it for a typo or a missing segment.',
        ref: sentUrl,
        fix: { target: 'connection' },
      },
    ];
  }

  // 2 — we recorded the endpoint changing since the last time this filled.
  const demotedBy = source?.validation?.demotedBy ?? [];
  if (demotedBy.includes('endpoint') && source?.validation?.lastProvenAt) {
    return [
      {
        confidence: 'likely',
        message:
          'This source filled before, and the endpoint changed after that. ' +
          'The exchange has refused since, so the change is the first thing to check.',
        ref: sentUrl,
        fix: { target: 'connection' },
      },
    ];
  }

  return [];
}

/**
 * The coverage half of {@link diagnoseEndpoint} — a bare 4xx with nothing specific
 * to say about it.
 *
 * Deliberately **appended last** and only `worth-checking`. The ranking in
 * `diagnoseTest` is fixed rather than scored precisely so a weak, always-available
 * cause cannot crowd out a strong one; a protocol mismatch visible in the payload is
 * `likely` and must still lead. Its first draft did lead, and displaced exactly those
 * — the specs caught it.
 */
function diagnoseEndpointCoverage(
  source: Partial<DemandSource> | null | undefined,
  capture: DriverCapture | undefined,
  status: number | undefined,
  refused: boolean,
): DiagnosisCause[] {
  if (!refused) return [];
  return [
    {
      confidence: 'worth-checking',
      message:
        `The host answered and refused (HTTP ${status ?? '4xx'}), so it was reached and the ` +
        'request was understood well enough to be rejected. Check the endpoint path and any ' +
        'routing or authentication parameters it needs.',
      ref: capture?.request?.url ?? source?.endpoint ?? undefined,
      fix: { target: 'connection' },
    },
  ];
}

/**
 * Rank candidate causes for a test that did not end in a fill.
 *
 * Precedence is fixed rather than scored: protocol mismatch, unresolved macros,
 * missing required fields, the exchange's own words, protocol shape. A scored
 * ranking would be unexplainable, and an unexplainable diagnosis is one the
 * operator cannot overrule.
 *
 * Returns `[]` when the test filled — there is nothing to diagnose.
 */
export function diagnoseTest(input: TestDiagnosisInput): DiagnosisCause[] {
  const outcome = deriveTestOutcome(input);
  if (outcome.filled.state === 'ok') return [];

  const facts = readTestBody(input.result);
  const capture = facts.capture;
  const sentBody = capture?.request?.body ? parseJson(capture.request.body) : undefined;
  const sentLeaves = sentBody && typeof sentBody === 'object' ? collectLeaves(sentBody) : null;

  const refused = outcome.answered.state === 'fail';
  const causes = [
    // The endpoint leads on a refusal: the host answered, so everything about
    // reachability is already ruled out and the path is what remains (#1008).
    ...diagnoseEndpoint(input.source, capture, refused),
    // Ahead of the payload causes: a discarded bid means the request worked and we decided.
    ...diagnoseRejectedAds(facts.rejectedAds, capture),
    ...diagnoseAdFieldGaps(facts.ads),
    ...diagnoseProtocolMismatch(input.source, sentLeaves),
    ...diagnoseUnresolvedMacros(input.source, capture, sentLeaves),
    ...diagnoseMissingFields(input.source, sentLeaves),
    ...diagnoseExchangeText(capture, refused, facts.noBidReason),
    ...diagnoseShapeMismatch(input.source, capture),
    // Last, and weakest: true of every refusal, so it must never outrank a cause
    // that points at something specific in the payload.
    ...diagnoseEndpointCoverage(input.source, capture, input.result?.status, refused),
  ];

  if (causes.length === 0) return [noSignalCause(outcome, facts, input.result != null)];
  return causes;
}
